// A20 C5 — "has anyone asked this install for its logs?"
//
// The ticket→job half of migration 42's link, rendered. Before this, a triager
// reading a report that came off a customer's own hardware had the reporter's
// console extract and nothing else, and getting the logs started with an email.
//
// ── WHY THIS IS ITS OWN CARD AND ITS OWN REQUEST ────────────────────────────
// Same posture as TicketTimeline: it spans a second table on a migration this
// database may not have, so a licence backend running ahead of its database
// must degrade to a sentence here rather than taking the report down with it.
// The card never throws upward and never blocks the page.
//
// ── ⚠ THERE IS NO DOWNLOAD BUTTON, AND THERE MUST NOT BE ────────────────────
// A support bundle is logs and configuration from a customer's own host, and it
// STAYS THERE. What travels is a reference — a path on their filesystem and a
// size — so an engineer knows the bundle exists and what to ask for. There is
// no upload endpoint, no bucket and no column for its content on our side, and
// the server strips content-shaped keys out of the ops result both on the way in
// and on the way out. If a future reader is tempted to add a "fetch bundle"
// action here, the thing to change is not this file.
//
// ── FIVE OUTCOMES, FIVE SENTENCES ───────────────────────────────────────────
// `no_install` is a FINDING, not an absence: an on-prem report whose instance id
// matches no active activation is worth a triager's attention. Rendering it the
// same as "we did not ask" would bury it.

import React, { useEffect, useState } from 'react';
import { Card, CardHead } from '@/components/shared/primitives';
import {
  bundleReference,
  formatBytes,
  getSupportBundle,
  SUPPORT_BUNDLE_REASON,
  type SupportBundleView,
} from './api';

const dim: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5 };
const mono: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  wordBreak: 'break-all',
  color: 'var(--text-primary)',
};
const row: React.CSSProperties = { display: 'flex', gap: 10, alignItems: 'baseline', marginTop: 8 };
const label: React.CSSProperties = { ...dim, minWidth: 96, flex: '0 0 auto' };

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; view: SupportBundleView };

/** The job's own progress, in words rather than a status enum. */
function jobProgress(status: string): string {
  switch (status) {
    case 'pending':
      return 'Queued — the install collects it on its next check-in.';
    case 'claimed':
      return 'The install has picked it up and is collecting now.';
    case 'done':
      return 'Collected on the install.';
    case 'failed':
      return 'The install reported that collection failed.';
    default:
      return status;
  }
}

export function SupportBundleCard({ ticketId }: { ticketId: string }) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    getSupportBundle(ticketId)
      .then((view) => live && setState({ kind: 'ready', view }))
      .catch((err: unknown) =>
        live &&
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Could not read the support-bundle status',
        }),
      );
    return () => {
      live = false;
    };
  }, [ticketId]);

  // NOT an on-prem report: there is no install to ask, there never was, and
  // saying so would be noise on every browser-filed ticket. Render nothing.
  if (state.kind === 'ready' && state.view.kind === 'absent') return null;

  return (
    <Card>
      <CardHead title="Support bundle" />
      <div style={{ padding: '0 16px 16px' }}>
        {state.kind === 'loading' && <p style={dim}>Checking…</p>}

        {state.kind === 'error' && (
          // A real failure of THIS read. Distinct from "no bundle", which is an
          // answer; this is the absence of one.
          <p style={dim}>{state.message}</p>
        )}

        {state.kind === 'ready' && state.view.kind === 'unmigrated' && (
          <p style={dim}>
            This licence server&rsquo;s database has not got migration 42, so bundle requests are
            not recorded here yet.
          </p>
        )}

        {state.kind === 'ready' && state.view.kind === 'no-link' && (
          <p style={dim}>{SUPPORT_BUNDLE_REASON[state.view.state]}</p>
        )}

        {state.kind === 'ready' && state.view.kind === 'job-gone' && (
          <p style={dim}>
            A bundle was requested for this install, but the job was removed when its organisation
            was purged. Nothing is outstanding.
          </p>
        )}

        {state.kind === 'ready' && state.view.kind === 'job' && (
          <>
            <p style={dim}>{jobProgress(state.view.job.status)}</p>
            <div style={row}>
              <span style={label}>Install</span>
              <span style={mono}>{state.view.job.instance_id}</span>
            </div>
            <div style={row}>
              <span style={label}>Job</span>
              <span style={mono}>{state.view.job.id}</span>
            </div>
            {(() => {
              const ref = bundleReference(state.view.job.result);
              if (!ref.path && ref.bytes == null) return null;
              return (
                <>
                  {ref.path && (
                    <div style={row}>
                      <span style={label}>Path on host</span>
                      <span style={mono}>{ref.path}</span>
                    </div>
                  )}
                  {ref.bytes != null && (
                    <div style={row}>
                      <span style={label}>Size</span>
                      <span style={mono}>{formatBytes(ref.bytes)}</span>
                    </div>
                  )}
                </>
              );
            })()}
            <p style={{ ...dim, marginTop: 12 }}>
              The bundle stays on the customer&rsquo;s host — only its path and size are reported
              here. Ask the customer for the file itself.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

export default SupportBundleCard;
